import { useEffect, useState } from 'react';
import {
  createExpenseClaim,
  listExpenseClaims,
  reviewExpenseClaim,
  type ExpenseClaim,
} from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { StatusBadge } from '../components/StatusBadge';
import { Icon } from '../components/Icon';

interface ExpenseItemInput {

  category: string;
  spentDate: string;
  amount: number;
  merchant: string;
  description: string;
}

export function Expenses() {
  const { session, can } = useAuth();
  const [claims, setClaims] = useState<ExpenseClaim[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'submitted' | 'approved' | 'rejected'>('all');
  const [msg, setMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Submit Claim Modal
  const [claimModal, setClaimModal] = useState(false);
  const [claimTitle, setClaimTitle] = useState('');
  const [claimCurrency, setClaimCurrency] = useState('INR');
  const [items, setItems] = useState<ExpenseItemInput[]>([
    {
      category: 'Travel',
      spentDate: new Date().toISOString().split('T')[0],
      amount: 0,
      merchant: '',
      description: '',
    },
  ]);

  async function loadData() {
    try {
      setLoading(true);
      const res = await listExpenseClaims({
        viewAll: can('expenses.manage') || can('organization.manage'),
      });
      setClaims(res.data || []);
    } catch (err: any) {
      setMsg({ type: 'error', text: err.message || 'Failed to load expense claims' });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void loadData();
  }, []);

  function addItemRow() {
    setItems([
      ...items,
      {
        category: 'Travel',
        spentDate: new Date().toISOString().split('T')[0],
        amount: 0,
        merchant: '',
        description: '',
      },
    ]);
  }

  function removeItemRow(index: number) {
    if (items.length <= 1) return;
    setItems(items.filter((_, i) => i !== index));
  }

  function updateItemRow(index: number, field: keyof ExpenseItemInput, val: any) {
    const next = [...items];
    next[index] = { ...next[index], [field]: val };
    setItems(next);
  }

  const totalClaimAmount = items.reduce((acc, curr) => acc + (Number(curr.amount) || 0), 0);

  async function handleSubmitClaim(e: React.FormEvent) {
    e.preventDefault();
    if (items.length === 0 || totalClaimAmount <= 0) {
      setMsg({ type: 'error', text: 'Please add at least one valid expense line item.' });
      return;
    }
    try {
      await createExpenseClaim({
        title: claimTitle,
        currency: claimCurrency,
        items: items.map((it) => ({
          category: it.category,
          spentDate: it.spentDate,
          amount: Number(it.amount),
          merchant: it.merchant || null,
          description: it.description || null,
        })),
      });
      setMsg({ type: 'success', text: 'Expense claim submitted successfully!' });
      setClaimModal(false);
      setClaimTitle('');
      setItems([
        {
          category: 'Travel',
          spentDate: new Date().toISOString().split('T')[0],
          amount: 0,
          merchant: '',
          description: '',
        },
      ]);
      await loadData();
    } catch (err: any) {
      setMsg({ type: 'error', text: err.message || 'Failed to submit claim' });
    }
  }

  async function handleReview(claimId: string, status: 'approved' | 'rejected') {
    try {
      await reviewExpenseClaim(claimId, status);
      setMsg({ type: 'success', text: `Expense claim ${status} successfully!` });
      await loadData();
    } catch (err: any) {
      setMsg({ type: 'error', text: err.message || 'Failed to review claim' });
    }
  }

  const filteredClaims = claims.filter((c) => {
    if (filter === 'all') return true;
    return c.status === filter;
  });

  return (
    <div>
      <div className="page-head">
        <div>
          <span className="eyebrow">
            <Icon name="wallet" size="xs" />
            <span>Finance & Claims</span>
          </span>
          <h1>Expense Reimbursements</h1>
        </div>
        <button className="btn-primary" onClick={() => setClaimModal(true)}>
          <Icon name="plus" size="xs" />
          <span>Submit Expense Claim</span>
        </button>
      </div>

      {msg && <div className={`banner ${msg.type}`}>{msg.text}</div>}

      <div className="panel">
        <div className="panel-head">
          <strong>Expense Claims Register</strong>
          <div className="toolbar">
            <select value={filter} onChange={(e) => setFilter(e.target.value as any)}>
              <option value="all">All Claims</option>
              <option value="submitted">Pending Approval</option>
              <option value="approved">Approved & Settled</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>
        </div>

        {loading ? (
          <div className="empty">Loading expense claims...</div>
        ) : (
          <div className="table-responsive">
            <table>
              <thead>
                <tr>
                  <th>Claim #</th>
                  <th>Submitted On</th>
                  <th>Employee</th>
                  <th>Title</th>
                  <th>Total Amount</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredClaims.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="empty">No expense claims found.</td>
                  </tr>
                ) : (
                  filteredClaims.map((c) => (
                    <tr key={c.id}>
                      <td className="mono"><strong>{c.claim_number}</strong></td>
                      <td className="mono">{c.created_at?.split('T')[0] || '—'}</td>
                      <td>
                        <strong>{c.first_name ? `${c.first_name} ${c.last_name || ''}` : session?.user.fullName}</strong>
                        <span className="muted mono" style={{ display: 'block', fontSize: 11 }}>{c.employee_code}</span>
                      </td>
                      <td>{c.title}</td>
                      <td className="mono money" style={{ fontWeight: 700 }}>
                        ₹{(c.total_amount || 0).toLocaleString('en-IN')}
                      </td>
                      <td><StatusBadge status={c.status} /></td>
                      <td>
                        {c.status === 'submitted' && (can('expenses.manage') || can('organization.manage')) ? (
                          <div style={{ display: 'flex', gap: 6 }}>
                            <button
                              className="btn-success btn-sm"
                              onClick={() => void handleReview(c.id, 'approved')}
                            >
                              <Icon name="checkCircle" size="xs" />
                              <span>Approve</span>
                            </button>
                            <button
                              className="btn-danger btn-sm"
                              onClick={() => void handleReview(c.id, 'rejected')}
                            >
                              <Icon name="xCircle" size="xs" />
                              <span>Reject</span>
                            </button>
                          </div>
                        ) : (
                          <span className="muted">—</span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Claim Modal */}
      {claimModal && (
        <div className="modal-backdrop" onClick={() => setClaimModal(false)}>
          <div className="modal-content wide" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <span className="eyebrow">Reimbursement Request</span>
                <h3>Submit Expense Claim</h3>
              </div>
              <button className="btn-icon" onClick={() => setClaimModal(false)} aria-label="Close">
                <Icon name="close" size="sm" />
              </button>
            </div>
            <form onSubmit={handleSubmitClaim}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div className="form-grid-2">
                  <div className="form-group">
                    <label>Claim Title / Purpose</label>
                    <input
                      type="text"
                      required
                      placeholder="e.g., Client visit travel & meals"
                      value={claimTitle}
                      onChange={(e) => setClaimTitle(e.target.value)}
                    />
                  </div>
                  <div className="form-group">
                    <label>Currency</label>
                    <select value={claimCurrency} onChange={(e) => setClaimCurrency(e.target.value)}>
                      <option value="INR">INR (₹ - Indian Rupee)</option>
                      <option value="USD">USD ($ - US Dollar)</option>
                    </select>
                  </div>
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 10 }}>
                  <span className="eyebrow" style={{ margin: 0 }}>Itemized Expenses</span>
                  <button type="button" className="btn-quiet btn-sm" onClick={addItemRow}>
                    <Icon name="plus" size="xs" />
                    <span>Add Line Item</span>
                  </button>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {items.map((row, idx) => (
                    <div key={idx} style={{ background: 'var(--bg-field)', padding: 14, borderRadius: 8, display: 'grid', gap: 10 }}>
                      <div className="form-grid-3">
                        <div className="form-group">
                          <label>Category</label>
                          <select value={row.category} onChange={(e) => updateItemRow(idx, 'category', e.target.value)}>
                            <option value="Travel">Travel & Fuel</option>
                            <option value="Meals">Meals & Food</option>
                            <option value="Accommodation">Hotel / Accommodation</option>
                            <option value="Supplies">Office Supplies</option>
                            <option value="Software">Software & Cloud</option>
                            <option value="Other">Other</option>
                          </select>
                        </div>
                        <div className="form-group">
                          <label>Spent Date</label>
                          <input type="date" required value={row.spentDate} onChange={(e) => updateItemRow(idx, 'spentDate', e.target.value)} />
                        </div>
                        <div className="form-group">
                          <label>Amount (₹)</label>
                          <input
                            type="number"
                            min="1"
                            required
                            value={row.amount || ''}
                            placeholder="0.00"
                            onChange={(e) => updateItemRow(idx, 'amount', e.target.value)}
                          />
                        </div>
                      </div>
                      <div className="form-grid-2">
                        <div className="form-group">
                          <label>Merchant / Vendor</label>
                          <input
                            type="text"
                            placeholder="e.g., Uber, Indigo, Amazon"
                            value={row.merchant}
                            onChange={(e) => updateItemRow(idx, 'merchant', e.target.value)}
                          />
                        </div>
                        <div className="form-group">
                          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <label>Description</label>
                            {items.length > 1 && (
                              <button
                                type="button"
                                className="btn-quiet btn-sm"
                                style={{ color: 'var(--status-alert)', padding: 0, border: 'none', background: 'transparent' }}
                                onClick={() => removeItemRow(idx)}
                              >
                                Remove
                              </button>
                            )}
                          </div>
                          <input
                            type="text"
                            placeholder="Brief purpose"
                            value={row.description}
                            onChange={(e) => updateItemRow(idx, 'description', e.target.value)}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div style={{ marginTop: 14, textAlign: 'right', fontSize: 15 }}>
                  Total Claim Amount: <strong className="mono money" style={{ color: 'var(--brand-primary)', fontSize: 18, marginLeft: 6 }}>₹{totalClaimAmount.toLocaleString('en-IN')}</strong>
                </div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 }}>
                <button type="button" className="btn-quiet" onClick={() => setClaimModal(false)}>Cancel</button>
                <button type="submit" className="btn-primary">Submit Claim for Review</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );

}
